import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource, MatTableDataSourcePaginator } from '@angular/material/table';
import { EMAILSETTING_DISPLAYED_COLUMNS } from '../../shared/constants/emailsetting-constant';
import { TimeZoneService } from '../../shared/services/time-zone.service';
import { LoginService } from '@agora/agora-ui-library';
import { UserSettingsService } from '../../shared/services/user-settings.service';
import { catchError, of, Subject, takeUntil, tap } from 'rxjs';
import { MessageService, SlbSeverity } from '@slb-dls/angular-material/notification';
import { EMAILSETTING_NOTIFICATION } from '../../shared/constants/emailsetting-constant';
import { EmailSettingHistory, EmailSettingTable } from 'src/app/shared/models/emailsetting';
import { Features } from '../../shared/models/feature';
import { FeatureType } from 'src/app/shared/constants/enum';
@Component({
  selector: 'app-email-setting',
  templateUrl: './email-setting.component.html',
  styleUrls: ['./email-setting.component.scss'],
})
export class EmailSettingComponent implements OnInit, OnDestroy {
  public dataSource: MatTableDataSource<EmailSettingTable, MatTableDataSourcePaginator>;
  public displayedColumns: string[] = EMAILSETTING_DISPLAYED_COLUMNS;
  public historyData: EmailSettingHistory[] = [];
  public tableData: EmailSettingTable[] = [];
  public viewMode = EMAILSETTING_NOTIFICATION.WITH;
  public currentZone = '';
  public isLoading = false;
  public disableSave = true;
  public isDirty = false;
  public primary = EMAILSETTING_NOTIFICATION.PRIMARY;
  public siteCheckBox = false;
  private feature: Features[] = [];
  private destroyed = new Subject();
  constructor(
    private timeZoneService: TimeZoneService,
    private loginService: LoginService,
    private userSettingsService: UserSettingsService,
    private messageService: MessageService
  ) {
    this.currentZone = this.timeZoneService.getTimeZone();
  }

  ngOnInit(): void {
    this.isDirty = false;
    this.disableSave = true;
    this.getEmailSetting();
    this.feature = JSON.parse(sessionStorage.getItem('feature') || '[]') as Features[];
  }

  public getEmailSetting(): void {
    this.isLoading = true;
    this.userSettingsService
      .getEmailSetting()
      .pipe(
        tap((organizationData: EmailSettingHistory[]) => {
          this.historyData = organizationData;
          this.createTableData(this.historyData);
        }),
        catchError(() => {
          this.isLoading = false;
          this.messageService.add({
            severity: SlbSeverity.Error,
            summary: EMAILSETTING_NOTIFICATION.SET_EMAIL_NOTIFICATION_HIST_ERROR,
            closable: true,
            sticky: true,
          });

          return of<EmailSettingHistory[]>({} as EmailSettingHistory[]);
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }
  public selectionChange(value: string, element: EmailSettingTable): void {
    this.isDirty = true;
    element.emailWithMedia = value;
    element.media = value === EMAILSETTING_NOTIFICATION.WITH;
    this.disableSave = false;
  }

  public saveSettings(): void {
    this.isLoading = true;
    const payload = this.tableData;
    this.userSettingsService
      .saveEmailSetting(payload)
      .pipe(
        tap(() => {
          this.isLoading = false;
          this.isDirty = false;
          this.disableSave = true;
          this.tableData = [];
          this.getEmailSetting();
          this.messageService.add({
            severity: SlbSeverity.Success,
            summary: 'Success',
            detail: EMAILSETTING_NOTIFICATION.SET_EMAIL_NOTIFICATION_SUCCESS,
          });
        }),
        catchError(() => {
          this.isLoading = false;
          this.messageService.add({
            severity: SlbSeverity.Error,
            summary: EMAILSETTING_NOTIFICATION.SET_EMAIL_NOTIFICATION_SAVE_ERROR,
            closable: true,
            sticky: true,
          });

          return of<EmailSettingTable[]>({} as EmailSettingTable[]);
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }
  public enableBtn(): void {
    this.isDirty = true;
    this.disableSave = false;
  }

  public checkBoxChange(emailSetting: EmailSettingTable): void {
    const hasEvents = !!emailSetting?.events;
    const hasNotification = !!emailSetting?.notification;
    const hasCsvfile = !!emailSetting?.csvfile;
    if (hasEvents && hasNotification && hasCsvfile) {
      emailSetting.site = true;
    } else {
      emailSetting.site = false;
    }
  }

  public disableCheckBox(emailSetting: EmailSettingTable, featureType: string): boolean {
    const siteValue = this.feature.some(x => x.featureType === featureType.toLowerCase() && x.gatewayId === emailSetting.gatewayId);

    return !siteValue;
  }

  public siteCheckBoxChange(): void {
    this.tableData.forEach(email => {
      const csvFile = this.getFeatureType(email.gatewayId, FeatureType[FeatureType.CsvFile]);
      email.events = this.siteCheckBox;
      email.notification = this.siteCheckBox;
      email.site = this.siteCheckBox;
      email.csvfile = csvFile ? this.siteCheckBox : false;
    });
    this.enableBtn();
  }

  public siteValueChange(emailSetting: EmailSettingTable): void {
    emailSetting.events = emailSetting.site;
    emailSetting.notification = emailSetting.site;
    emailSetting.csvfile = emailSetting.site;
    this.enableBtn();
    const allSiteValue = this.tableData?.some(x => x.site === false || x.site === null);
    if (allSiteValue) {
      this.siteCheckBox = false;
    } else {
      this.siteCheckBox = true;
    }
  }
  public resetSettings(): void {
    this.isLoading = true;
    this.tableData = [];
    this.createTableData(this.historyData);
    this.isDirty = false;
    this.disableSave = true;
  }

  ngOnDestroy(): void {
    this.destroyed.next(true);
  }

  private createTableData(emailData: EmailSettingHistory[]): void {
    emailData.forEach(email => {
      this.tableData.push({
        gatewayName: email.gatewayName,
        gatewayId: email.gatewayId,
        events: email.events,
        notification: email.notification,
        emailWithMedia: email.media ? EMAILSETTING_NOTIFICATION.WITH : EMAILSETTING_NOTIFICATION.WITHOUT,
        media: email.media,
        createdAt: email.createdAt,
        assetName: email.assetName,
        csvfile: email.csvfile,
        site: email.events && email.csvfile && email.notification,
      });
    });
    this.dataSource = new MatTableDataSource(this.tableData);
    this.isLoading = false;
  }

  private getFeatureType(gatewayId: string, featureType: string): boolean {
    return this.feature.some(x => x.featureType?.toLowerCase() === featureType.toLowerCase() && x.gatewayId === gatewayId);
  }
}
